/* cSpell:ignore noneditable checkmarks ampm */
import React from "react";

import { faCircleInfo } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleCheck, faCircleXmark } from "@performyard/fontawesome/pro-solid-svg-icons";
import {
	Checkbox,
	FormDatePicker,
	FormField,
	FormInputNumeric,
	FormSingleSelect,
	FormSubscribe,
	Icon,
	SortableOrderedList,
	Text,
	Tooltip,
	cn,
	usePyForm,
} from "@performyard/py-components";

import { DEFAULT_DUE_TIME } from "../utils/constants";
import { formatDateViewOnly } from "../utils/dateFormatUtils";
import { STATIC_EMPLOYEE_OPTIONS, renderEmployeeOrderedItemReadOnly } from "../utils/employeeSelectionUtils";

import { type EmployeeValue, MultipleOrderedSelectEmployee } from "../../Inputs/SelectEmployee";
import type { ReviewFormContainerProps } from "../ReviewFormContainer";
import { ReviewFormContainer } from "../ReviewFormContainer";

export type Form360EditableField =
	| "whoCanAddForm"
	| "formAuthor"
	| "approval"
	| "formSigners"
	| "dueDatesToggle"
	| "authorsDueDate"
	| "signersDueDate"
	| "makeVisibleWhenFinished"
	| "allow360RequestMessage"
	| "redactAuthors"
	| "makeFormInactive";

export type Form360CardProps = {
	/**
	 * Minimum allowed due date (e.g., review cycle start date)
	 * Only applies to fixed/static dates (when dueDatesToggle is checked), not relative dates
	 * When set, date pickers will disable dates before this date
	 */
	minimumDueDate?: Date;
	/**
	 * Company default review-cycle due time (hour of day, 0-23) that fixed due dates default to.
	 * @default DEFAULT_DUE_TIME
	 */
	defaultDueTime?: number;
	/**
	 * Whether all fields are view-only
	 * @default false
	 */
	readOnly?: boolean;
	/**
	 * Specific fields that should be editable when readOnly is true
	 * Only applies when readOnly is true - these fields will be editable while others remain view-only
	 * @example ["whoCanAddForm", "approval"]
	 */
	editableFields?: Array<Form360EditableField>;
	/**
	 * Hides the "Redact authors" checkbox for roles without permission to change it. A form that is
	 * already redacted shows a read-only indicator instead, and its saved value is left untouched.
	 */
	hideRedactAuthorsControl?: boolean;
	/**
	 * Optional CSS classes to apply to the container
	 */
	dangerouslySetClassName?: string;
};

const WHO_CAN_ADD_OPTIONS = [
	{ label: "Any Employee", value: "Any Employee" },
	{ label: "Subject", value: "Subject" },
	{ label: "Manager", value: "Manager" },
	{ label: "Admin", value: "Admin" },
];

const APPROVAL_OPTIONS = [
	{ label: "No Approval Needed", value: "No Approval Needed" },
	{ label: "Require Approval from Admins", value: "Require Approval from Admins" },
	{ label: "Require Approval from Admins or Managers", value: "Require Approval from Admins or Managers" },
];

const FORM_AUTHOR_OPTIONS = [
	{ label: "Any Employee", value: "Any Employee" },
	{ label: "Subject", value: "Subject" },
];

/**
 * Form360Card displays the form fields for a 360 review form.
 * This should be wrapped in a PYForm component with the form360Schema.
 */
export const Form360Card = ({
	minimumDueDate,
	defaultDueTime = DEFAULT_DUE_TIME,
	readOnly = false,
	editableFields = [],
	hideRedactAuthorsControl = false,
	dangerouslySetClassName,
}: Form360CardProps) => {
	// Helper to check if a field is editable
	const isFieldEditable = (fieldName: string) => {
		if (!readOnly) return true; // All fields editable when not in readOnly mode
		return editableFields.includes(fieldName as any); // Field is editable if in editableFields list
	};

	const { id: formId, ...form } = usePyForm();

	// Date conversion logic - runs side effects to convert between relative and fixed dates
	const DateConversionSubscriber = () => (
		<FormSubscribe
			id="form-360-date-conversion"
			selector={(state) => [
				state.values.dueDatesToggle,
				state.values.authorsDueDate,
				state.values.signersDueDate,
			]}
		>
			{([dueDatesToggle, authorsDueDate, signersDueDate]) => {
				// Convert dates when there's a type mismatch (matches EasyAddForm pattern)
				// Use minimumDueDate as reference if provided, otherwise use today at 5PM
				// This ensures conversions are consistent relative to the same base date
				const now = new Date();
				now.setHours(defaultDueTime, 0, 0, 0);
				const effectiveMinimumDate = minimumDueDate && minimumDueDate > now ? minimumDueDate : now;

				// Convert from relative (numbers) to static (Date objects)
				// Only when toggle is ON but values are still numbers
				if (dueDatesToggle && typeof authorsDueDate === "number") {
					const staticAuthorDate = new Date(effectiveMinimumDate);
					staticAuthorDate.setDate(staticAuthorDate.getDate() + authorsDueDate);
					form.setFieldValue("authorsDueDate", staticAuthorDate);

					if (typeof signersDueDate === "number") {
						const staticSignerDate = new Date(staticAuthorDate);
						staticSignerDate.setDate(staticSignerDate.getDate() + signersDueDate);
						form.setFieldValue("signersDueDate", staticSignerDate);
					}
				}

				// Convert from static (Date objects) to relative (numbers)
				// Only when toggle is OFF but values are still Dates
				if (!dueDatesToggle && authorsDueDate instanceof Date) {
					const daysDiff = Math.floor(
						(authorsDueDate.getTime() - effectiveMinimumDate.getTime()) / (1000 * 60 * 60 * 24)
					);
					const relativeDays = daysDiff < 0 ? 0 : daysDiff;
					form.setFieldValue("authorsDueDate", relativeDays);

					if (signersDueDate instanceof Date && authorsDueDate instanceof Date) {
						const signerDaysDiff = Math.floor(
							(signersDueDate.getTime() - authorsDueDate.getTime()) / (1000 * 60 * 60 * 24)
						);
						const relativeSignerDays = signerDaysDiff < 0 ? 0 : signerDaysDiff;
						form.setFieldValue("signersDueDate", relativeSignerDays);
					}
				}

				return null;
			}}
		</FormSubscribe>
	);

	// Readonly view with no editable fields
	if (readOnly && editableFields.length === 0) {
		return (
			<>
				<DateConversionSubscriber />
				<FormSubscribe
					id="form-360-readonly"
					selector={(state) => ({
						whoCanAddForm: state.values.whoCanAddForm,
						formAuthor: state.values.formAuthor,
						approval: state.values.approval,
						formSigners: state.values.formSigners || [],
						dueDatesToggle: state.values.dueDatesToggle,
						authorsDueDate: state.values.authorsDueDate,
						signersDueDate: state.values.signersDueDate,
						makeVisibleWhenFinished: state.values.makeVisibleWhenFinished,
						allow360RequestMessage: state.values.allow360RequestMessage,
						redactAuthors: state.values.redactAuthors,
						makeFormInactive: state.values.makeFormInactive,
					})}
				>
					{({
						whoCanAddForm,
						formAuthor,
						approval,
						formSigners,
						dueDatesToggle,
						authorsDueDate,
						signersDueDate,
						makeVisibleWhenFinished,
						allow360RequestMessage,
						redactAuthors,
						makeFormInactive,
					}) => {
						const showDueDates = formSigners.length > 0;

						return (
							<div className={cn("flex flex-col gap-4", dangerouslySetClassName)}>
								<div className="grid grid-cols-[180px_1fr] gap-2">
									<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
										Who Can Add This Form
									</Text>
									<Text size="paragraph-3" color="neutral-9">
										{whoCanAddForm}
									</Text>
								</div>

								{whoCanAddForm === "Subject" && formAuthor && (
									<div key="form-author-readonly" className="grid grid-cols-[180px_1fr] gap-2">
										<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
											Form Author
										</Text>
										<Text size="paragraph-3" color="neutral-9">
											{formAuthor}
										</Text>
									</div>
								)}

								<div className="grid grid-cols-[180px_1fr] gap-2">
									<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
										Approval
									</Text>
									<Text size="paragraph-3" color="neutral-9">
										{approval}
									</Text>
								</div>

								{formSigners.length > 0 && (
									<div key="form-signers-readonly" className="grid grid-cols-[180px_1fr] gap-2">
										<Text
											size="paragraph-3"
											weight="semi-bold"
											color="neutral-9"
											dangerouslySetClassName="flex items-start pt-1"
										>
											Form Signers
										</Text>
										<SortableOrderedList<EmployeeValue>
											values={formSigners}
											readOnly
											renderItem={renderEmployeeOrderedItemReadOnly}
											getItemId={(employee) => employee.id}
										/>
									</div>
								)}

								<div className="grid grid-cols-[180px_1fr] gap-2">
									<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
										Due Dates
									</Text>
									<div className="flex items-center gap-2">
										<Icon
											icon={dueDatesToggle ? faCircleCheck : faCircleXmark}
											size={20}
											color={dueDatesToggle ? "green-5" : "red-5"}
										/>
										<Text size="paragraph-3" color="neutral-9">
											Change due dates to fixed calendar dates
										</Text>
									</div>
								</div>

								{authorsDueDate != null && (
									<div key="authors-due-date-readonly" className="grid grid-cols-[180px_1fr] gap-2">
										<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
											Authors Due Date
										</Text>
										{!dueDatesToggle && typeof authorsDueDate === "number" ? (
											<Text size="paragraph-3" color="neutral-9">
												{authorsDueDate} Days from{" "}
												<span className="font-semibold">Date Form Added</span>
											</Text>
										) : (
											formatDateViewOnly(authorsDueDate)
										)}
									</div>
								)}

								{showDueDates && signersDueDate != null && (
									<div key="signers-due-date-readonly" className="grid grid-cols-[180px_1fr] gap-2">
										<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
											Signers Due Date
										</Text>
										{!dueDatesToggle && typeof signersDueDate === "number" ? (
											<Text size="paragraph-3" color="neutral-9">
												{signersDueDate} Days from{" "}
												<span className="font-semibold">Authors Due Date</span>
											</Text>
										) : (
											formatDateViewOnly(signersDueDate)
										)}
									</div>
								)}

								<div className="flex flex-col gap-2">
									<div className="flex items-center gap-2">
										<Icon
											icon={makeVisibleWhenFinished ? faCircleCheck : faCircleXmark}
											size={20}
											color={makeVisibleWhenFinished ? "green-5" : "red-5"}
										/>
										<Text size="paragraph-3" color="neutral-9">
											Make visible to the subject when form is completed
										</Text>
									</div>
									<div className="flex items-center gap-2">
										<Icon
											icon={allow360RequestMessage ? faCircleCheck : faCircleXmark}
											size={20}
											color={allow360RequestMessage ? "green-5" : "red-5"}
										/>
										<Text size="paragraph-3" color="neutral-9">
											Allow 360 request message
										</Text>
									</div>
									<div className="flex items-center gap-2">
										<Icon
											icon={redactAuthors ? faCircleCheck : faCircleXmark}
											size={20}
											color={redactAuthors ? "green-5" : "red-5"}
										/>
										<Text size="paragraph-3" color="neutral-9">
											Redact authors
										</Text>
									</div>
									<div className="flex items-center gap-2">
										<Icon
											icon={makeFormInactive ? faCircleCheck : faCircleXmark}
											size={20}
											color={makeFormInactive ? "green-5" : "red-5"}
										/>
										<Text size="paragraph-3" color="neutral-9">
											Make form inactive
										</Text>
									</div>
								</div>
							</div>
						);
					}}
				</FormSubscribe>
			</>
		);
	}

	// Editable/mixed view
	return (
		<div className={cn("grid grid-cols-[180px_1fr] items-start gap-2", dangerouslySetClassName)}>
			<DateConversionSubscriber />

			<Text
				size="paragraph-3"
				weight="semi-bold"
				color="neutral-9"
				overrideElement="label"
				htmlFor="whoCanAddForm"
				dangerouslySetClassName="flex h-8 items-center"
			>
				Who Can Add This Form
			</Text>
			<div>
				<FormSingleSelect
					id="whoCanAddForm"
					placeholder="Select who can add"
					options={WHO_CAN_ADD_OPTIONS}
					disabled={!isFieldEditable("whoCanAddForm")}
				/>
			</div>

			<FormSubscribe id="form-360-form-author-conditional" selector={(state) => [state.values.whoCanAddForm]}>
				{([whoCanAddForm]) =>
					whoCanAddForm === "Subject" && (
						<React.Fragment key="form-author">
							<Text
								size="paragraph-3"
								weight="semi-bold"
								color="neutral-9"
								overrideElement="label"
								htmlFor="formAuthor"
								dangerouslySetClassName="flex h-8 items-center"
							>
								Form Author
							</Text>
							<div>
								<FormSingleSelect
									id="formAuthor"
									placeholder="Select form author"
									options={FORM_AUTHOR_OPTIONS}
									disabled={!isFieldEditable("formAuthor")}
								/>
							</div>
						</React.Fragment>
					)
				}
			</FormSubscribe>

			<Text
				size="paragraph-3"
				weight="semi-bold"
				color="neutral-9"
				overrideElement="label"
				htmlFor="approval"
				dangerouslySetClassName="flex h-8 items-center"
			>
				Approval
			</Text>
			<div>
				<FormSingleSelect
					id="approval"
					placeholder="Select approval type"
					options={APPROVAL_OPTIONS}
					disabled={!isFieldEditable("approval")}
				/>
			</div>

			<Text
				size="paragraph-3"
				weight="semi-bold"
				color="neutral-9"
				overrideElement="label"
				htmlFor="formSigners"
				dangerouslySetClassName="flex h-8 items-center"
			>
				Form Signers
			</Text>
			<div>
				<FormField<EmployeeValue[]>
					id="formSigners"
					input={(field) => (
						<MultipleOrderedSelectEmployee
							value={field.value || []}
							onChange={field.onChange}
							placeholder="Search for signers..."
							disabled={!isFieldEditable("formSigners")}
							staticOptions={STATIC_EMPLOYEE_OPTIONS}
							closeOnSelect={true}
						/>
					)}
				/>
			</div>

			<Text
				size="paragraph-3"
				weight="semi-bold"
				color="neutral-9"
				overrideElement="label"
				htmlFor={`${formId}-dueDatesToggle`}
				dangerouslySetClassName="flex h-8 items-center"
			>
				Due Dates
			</Text>
			<div>
				<FormField<boolean>
					id="dueDatesToggle"
					input={(field) => (
						<div className="flex h-8 items-center gap-2">
							<Checkbox
								id={`${formId}-dueDatesToggle`}
								checked={field.value === true}
								onCheckedChange={(checked) => field.onChange(checked)}
								disabled={!isFieldEditable("dueDatesToggle")}
							/>
							<Text
								size="paragraph-3"
								color="neutral-9"
								overrideElement="label"
								htmlFor={`${formId}-dueDatesToggle`}
								dangerouslySetClassName="cursor-pointer"
							>
								Change due dates to a fixed calendar date
							</Text>
						</div>
					)}
				/>
			</div>

			<FormSubscribe
				id="form-360-due-dates-conditional"
				selector={(state) => [(state.values.formSigners || []).length > 0, state.values.dueDatesToggle]}
			>
				{([showDueDates, dueDatesToggle]) => (
					<>
						<Text
							size="paragraph-3"
							weight="semi-bold"
							color="neutral-9"
							overrideElement="label"
							htmlFor="authorsDueDate"
							dangerouslySetClassName="flex h-8 items-center"
						>
							Authors Due Date
						</Text>
						<div>
							{!dueDatesToggle ? (
								<FormField<number | null>
									id="authorsDueDate"
									input={() => (
										<div className="flex items-center gap-2">
											<div className="w-24">
												<FormInputNumeric
													id="authorsDueDate"
													disabled={!isFieldEditable("authorsDueDate")}
													allowEmpty={false}
													min={0}
												/>
											</div>
											<span
												className="text-[14px] leading-4 font-normal text-neutral-7"
												style={{ fontFamily: '"Source Sans Pro"' }}
											>
												Days from <span className="font-semibold">Date Form Added</span>
											</span>
										</div>
									)}
								/>
							) : (
								<FormDatePicker
									id="authorsDueDate"
									disabled={!isFieldEditable("authorsDueDate")}
									startDate={minimumDueDate}
									showTimeDropdown={true}
									defaultTime={defaultDueTime}
								/>
							)}
						</div>

						{showDueDates && (
							<>
								<Text
									size="paragraph-3"
									weight="semi-bold"
									color="neutral-9"
									overrideElement="label"
									htmlFor="signersDueDate"
									dangerouslySetClassName="flex h-8 items-center"
								>
									Signers Due Date
								</Text>
								<div>
									{!dueDatesToggle ? (
										<FormField<number | null>
											id="signersDueDate"
											input={() => (
												<div className="flex items-center gap-2">
													<div className="w-24">
														<FormInputNumeric
															id="signersDueDate"
															disabled={!isFieldEditable("signersDueDate")}
															allowEmpty={false}
															min={0}
														/>
													</div>
													<span
														className="text-[14px] leading-4 font-normal text-neutral-7"
														style={{ fontFamily: '"Source Sans Pro"' }}
													>
														Days from{" "}
														<span className="font-semibold">Authors Due Date</span>
													</span>
												</div>
											)}
										/>
									) : (
										<FormDatePicker
											id="signersDueDate"
											disabled={!isFieldEditable("signersDueDate")}
											startDate={minimumDueDate}
											showTimeDropdown={true}
											defaultTime={defaultDueTime}
										/>
									)}
								</div>
							</>
						)}
					</>
				)}
			</FormSubscribe>

			<div className="col-span-2 flex flex-col gap-2">
				<FormField<boolean>
					id="makeVisibleWhenFinished"
					input={(field) => (
						<div className="flex items-center gap-2">
							<Checkbox
								id={`${formId}-makeVisibleWhenFinished`}
								checked={field.value === true}
								onCheckedChange={(checked) => field.onChange(checked)}
								disabled={!isFieldEditable("makeVisibleWhenFinished")}
							/>
							<Text
								size="paragraph-3"
								color="neutral-9"
								overrideElement="label"
								htmlFor={`${formId}-makeVisibleWhenFinished`}
								dangerouslySetClassName="cursor-pointer"
							>
								Make visible to the subject when form is completed
							</Text>
						</div>
					)}
				/>
				<FormField<boolean>
					id="allow360RequestMessage"
					input={(field) => (
						<div className="flex items-center gap-2">
							<Checkbox
								id={`${formId}-allow360RequestMessage`}
								checked={field.value === true}
								onCheckedChange={(checked) => field.onChange(checked)}
								disabled={!isFieldEditable("allow360RequestMessage")}
							/>
							<Text
								size="paragraph-3"
								color="neutral-9"
								overrideElement="label"
								htmlFor={`${formId}-allow360RequestMessage`}
								dangerouslySetClassName="cursor-pointer"
							>
								Allow 360 request message
							</Text>
						</div>
					)}
				/>
				<FormField<boolean>
					id="redactAuthors"
					input={(field) =>
						hideRedactAuthorsControl ? (
							field.value === true ? (
								<div className="flex items-center gap-2">
									<Icon icon={faCircleCheck} size={20} color="green-5" />
									<Text size="paragraph-3" color="neutral-9">
										Redact authors
									</Text>
								</div>
							) : null
						) : (
							<div className="flex items-center gap-2">
								<Checkbox
									id={`${formId}-redactAuthors`}
									checked={field.value === true}
									onCheckedChange={(checked) => field.onChange(checked)}
									disabled={!isFieldEditable("redactAuthors")}
								/>
								<Text
									size="paragraph-3"
									color="neutral-9"
									overrideElement="label"
									htmlFor={`${formId}-redactAuthors`}
									dangerouslySetClassName="cursor-pointer"
								>
									Redact authors
								</Text>
							</div>
						)
					}
				/>
				<FormField<boolean>
					id="makeFormInactive"
					input={(field) => (
						<div className="flex items-center gap-2">
							<Checkbox
								id={`${formId}-makeFormInactive`}
								checked={field.value === true}
								onCheckedChange={(checked) => field.onChange(checked)}
								disabled={!isFieldEditable("makeFormInactive")}
							/>
							<Text
								size="paragraph-3"
								color="neutral-9"
								overrideElement="label"
								htmlFor={`${formId}-makeFormInactive`}
								dangerouslySetClassName="cursor-pointer flex items-center gap-1"
							>
								Make form inactive
								<Tooltip content="Inactive forms do not appear as an option when adding 360 forms to reviews until manually reactivated.">
									<Icon
										icon={faCircleInfo}
										size={16}
										color="neutral-7"
										aria-label="More information"
									/>
								</Tooltip>
							</Text>
						</div>
					)}
				/>
			</div>
		</div>
	);
};

Form360Card.displayName = "Form360Card";

/**
 * Combined props for Form360 wrapper component
 */
export type Form360Props = Omit<ReviewFormContainerProps, "children"> &
	Omit<Form360CardProps, "dangerouslySetClassName"> & {
		/**
		 * Optional CSS classes to apply to the outer container
		 */
		containerClassName?: string;
		/**
		 * Optional CSS classes to apply to the inner card
		 */
		cardClassName?: string;
	};

/**
 * Form360 combines ReviewFormContainer and Form360Card
 * into a single component for easier usage.
 *
 * This is the recommended component to use. It includes the container with
 * header, delete button, and template icon functionality.
 */
export const Form360 = ({
	// Container props
	name,
	fromTemplate,
	onDelete,
	containerClassName,
	"data-testid": dataTestId,
	// Card props
	minimumDueDate,
	defaultDueTime,
	readOnly,
	editableFields,
	hideRedactAuthorsControl,
	cardClassName,
}: Form360Props) => (
	<ReviewFormContainer
		name={name}
		fromTemplate={fromTemplate}
		onDelete={onDelete}
		dangerouslySetClassName={containerClassName}
		data-testid={dataTestId}
	>
		<Form360Card
			minimumDueDate={minimumDueDate}
			defaultDueTime={defaultDueTime}
			readOnly={readOnly}
			editableFields={editableFields}
			hideRedactAuthorsControl={hideRedactAuthorsControl}
			dangerouslySetClassName={cardClassName}
		/>
	</ReviewFormContainer>
);

Form360.displayName = "Form360";
